import React, { PureComponent } from 'react'
import {
    Checkbox,
    // @ts-ignore
    ShimmeredDetailsList, SelectionMode, DetailsListLayoutMode, ConstrainMode, Text, TextField, IColumn, Label, DefaultButton, MessageBarType,
} from '@fluentui/react'
import history from 'helpers/history'
import { handleRenderColumn, isValidDate } from 'helpers/methods/common'
import Status from 'types/status'
// @ts-ignore
// eslint-disable-next-line import/named
import { AppProps } from 'app'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import CancelRequestError from 'requests/errors/cancelRequestError'
import NotImplementedError from 'requests/errors/notImplementedError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import flattenObj from 'helpers/methods/flattenObj'
import Card from 'components/containers/card'
import styles from 'styles/pages/export.module.scss'
import { Columns } from 'react-bulma-components'
import Collaborator from 'requests/objects/collaborator'
import Time from 'helpers/methods/time'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import { ParamElement } from 'requests/objects/param'
import ExportDto, { ErrorExportDto } from 'requests/objects/exportDto'
import classNames from 'classnames'

/** @debug {AppProps} */

/**
 * @typedef {object} SearchParamsType
 * @property {string} company company
 * @property {string} analyticalCenter analyticalCenter
 * @property {string} manager manager
 * @property {boolean} areCollaboratorsLeft areCollaboratorsLeft
 * @property {boolean} onlyDriver onlyDriver
 */

/**
 * @typedef {object} FilterParamsType
 * @property {string} registrationNumber registrationNumber
 * @property {string} lastname lastname
 * @property {string} firstname firstname
 */

/**
 * @augments {PureComponent<AppProps>} extends
 */
export default class Export extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            /** @type {Status} Current status of the component */
            status: Status.IDLE,
            /** @type {IColumn[]} Columns displayed */
            columns: [],
            /** @type {Collaborator[]} Items found in API */
            items: [],
            /** @type {Collaborator[]} Initial items found in API */
            initItems: [],
            /** @type {SearchParamsType} Params to search */
            searchParams: {
                company: null,
                analyticalCenter: null,
                manager: null,
                areCollaboratorsLeft: null,
                onlyDriver: null,
            },
            /** @type {FilterParamsType} Params to filter */
            filterParams: {
                registrationNumber: '',
                lastname: '',
                firstname: '',
            },
            /** @type {ExportDto} export params */
            exportDto: new ExportDto(),
            /** @type {ErrorExportDto} export params */
            errorField: new ErrorExportDto(),
            /** @type {ParamElement[]} list of analytical centers */
            analyticalCenters: [],
            /** @type {ParamElement[]} list of managers */
            managers: [],
            /** @type {boolean} isCollaboratorsChecked */
            isCollaboratorsChecked: false,
            /** @type {boolean} displayCollaborators */
            displayCollaborators: true,
        }

        this.submitInput = React.createRef()

        /** @type {NodeJS.Timeout} Time out to handle apply of searchParams */
        this.timeOutParams = null
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        this.init()
    }

    /**
     * @inheritdoc
     * @param {object} prevProps Previous Props
     * @param {object} prevState Previous State
     */
    // @ts-ignore
    componentDidUpdate(prevProps, prevState) {
        const { searchParams } = this.state

        if (JSON.stringify(prevState.searchParams) !== JSON.stringify(searchParams)) {
            // Apply changement with delay to prevent lag
            clearTimeout(this.timeOutParams)

            this.timeOutParams = setTimeout(() => {
                history.replace({ search: '' })
                this.syncSearchParamsInHistory()
            }, 250)
        }
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        this.collaboratorsHandlerGetAll?.cancel()
        this.createExportHandler?.cancel()
    }

    /**
     * checkbox
     * @param {Collaborator} collaborator collaborator
     * @returns {JSX.Element} checkbox
     */
    // eslint-disable-next-line class-methods-use-this
    onCheckBoxRender(collaborator) {
        return (
            <Checkbox
                checked={collaborator.isSelected}
            />
        )
    }

    /**
     * On filter change
     * @param {"company" | "manager" | "analyticalCenter"} type type
     * @param {object} option option
     */
    onFilterChange(type, option) {
        // avoid filter two times
        if (option.key && option.index === undefined)
            return
        const { searchParams } = this.state
        const newParams = {}

        switch (type) {
            case 'company':
                newParams.company = option.key
                newParams.analyticalCenter = null
                newParams.manager = null
                break
            case 'analyticalCenter':
                newParams.analyticalCenter = option.key
                newParams.manager = null
                if (option.key)
                    newParams.company = option.codeCompany
                break
            case 'manager':
                newParams.manager = option.key
                if (option.key) {
                    newParams.company = option.codeCompany
                    newParams.analyticalCenter = option.codeCentre
                }
                break
            default:
                return
        }

        this.setState({
            searchParams: {
                ...searchParams,
                ...newParams,
            },
        })
    }

    /**
     * Setup breadcrumb elements
     */
    setupBreadcrumb() {
        const { setBreadcrumb } = this.props

        setBreadcrumb([
            { text: 'Export', key: 'user', isCurrentItem: true },
        ])
    }

    /**
     * Filter collaborators
     * @param {Collaborator[]} collaborators collaborators
     */
    setFilterCollaborators(collaborators) {
        const { filterParams } = this.state

        const filterItems = collaborators.filter(collaborator => {
            const { registrationNumber, lastname, firstname } = collaborator

            if (filterParams.registrationNumber && !registrationNumber.includes(filterParams.registrationNumber))
                return false
            if (filterParams.lastname && !lastname.includes(filterParams.lastname))
                return false
            if (filterParams.firstname && !firstname.includes(filterParams.firstname))
                return false

            return true
        })

        this.setState({
            initItems: collaborators,
            items: filterItems.map(x => this.flattenObj(x)),
            status: Status.RESOLVED,
        })
    }

    /**
     * Init list of managers and analytical centers
     */
    initManagersAndAnalyticalCenters() {
        const { searchParams } = this.state
        const { param: { managers, analyticalCenters } } = this.props

        const newState = {
            analyticalCenters,
            managers,
        }
        if (searchParams.company) {
            newState.managers = managers.filter(manager => manager.codeCompany === searchParams.company)
            newState.analyticalCenters = analyticalCenters.filter(analyticalCenter => analyticalCenter.codeCompany === searchParams.company)
        }
        if (searchParams.analyticalCenter)
            newState.managers = managers.filter(manager => manager.codeCentre === searchParams.analyticalCenter)

        this.setState(newState)
    }

    /**
     * Sync SearchParams in navigation history
     */
    syncSearchParamsInHistory() {
        const { searchParams } = this.state

        const queryParams = new URLSearchParams(window.location.search)

        // eslint-disable-next-line no-restricted-syntax
        for (const key in searchParams)
            if (searchParams[key] !== null && searchParams[key] !== '' && searchParams[key]?.length !== 0)
                if (Array.isArray(searchParams[key]))
                    // eslint-disable-next-line no-restricted-syntax
                    for (const element of searchParams[key])
                        queryParams.append(key, element)
                else
                    queryParams.set(key, searchParams[key])

        history.replace({
            search: queryParams.toString(),
        })

        this.initManagersAndAnalyticalCenters()

        this.search()
    }

    /**
     * Init Page
     */
    init() {
        const { setMessageBar, setCommand } = this.props

        setMessageBar({ isDisplayed: false })
        setCommand([])
        this.setupBreadcrumb()

        if (!window.location.search) {
            this.syncSearchParamsInHistory()
        } else {
            const queryParams = new URLSearchParams(window.location.search)

            // Reset Filter
            this.setState({
                /** @type {SearchParamsType} */
                searchParams: {
                    company: queryParams.get('company') || null,
                    analyticalCenter: queryParams.get('analyticalCenter') || null,
                    manager: queryParams.get('manager') || null,
                    areCollaboratorsLeft: queryParams.get('areCollaboratorsLeft') === 'true',
                    onlyDriver: queryParams.get('onlyDriver') === 'true',
                },
            })
        }

        this.setState({ columns: this.buildColumns() })
    }

    /**
     * Init <DetailList>
     * @returns {IColumn[]} Columns
     */
    buildColumns() {
        return [
            {
                key: 'isSelected',
                name: '',
                minWidth: 35,
                maxWidth: 35,
                isResizable: true,
                onRender: collaborator => this.onCheckBoxRender(collaborator),
                className: styles['centered-item'],
            },
            {
                key: 'registrationNumber',
                name: 'Matricule',
                fieldName: 'registrationNumber',
                minWidth: 70,
                maxWidth: 100,
                isResizable: true,
                styles: {
                    cellTooltip: {
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'row',
                    },
                },
                className: styles['centered-item'],
            },
            {
                key: 'firstname',
                name: 'Prénom',
                fieldName: 'firstname',
                minWidth: 100,
                maxWidth: 200,
                isResizable: true,
            },
            {
                key: 'lastname',
                name: 'Nom',
                fieldName: 'lastname',
                minWidth: 100,
                maxWidth: 200,
                isResizable: true,
            },
        ]
    }

    /**
     * Search elements
     */
    search() {
        const { status } = this.state
        if (status === Status.PENDING)
            return

        this.setState({
            status: Status.PENDING,
        }, async () => {
            const { exportHandler } = this.props
            const { searchParams, isCollaboratorsChecked } = this.state

            if (!searchParams.company) {
                this.setState({
                    items: [],
                    initItems: [],
                    status: Status.RESOLVED,
                })
                return
            }

            try {
                this.collaboratorsHandlerGetAll = exportHandler.getCollaborators(searchParams)
                const collaborators = await this.collaboratorsHandlerGetAll.fetch()
                const items = collaborators?.length ? collaborators.map(
                    collaborator => new Collaborator({
                        ...collaborator,
                        isSelected: isCollaboratorsChecked,
                    }),
                ) : []
                this.setFilterCollaborators(items)
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                    default:
                        this.setState({
                            items: [],
                            // eslint-disable-next-line react/no-unused-state
                            initItems: [],
                            status: Status.REJECTED,
                        })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    /**
     * Export elements
     */
    export() {
        const { initItems, exportDto } = this.state
        const { setModal, setMessageBar, exportHandler } = this.props

        const collaborators = initItems.reduce((returnItems, item) => {
            if (item.isSelected)
                returnItems.push(item.registrationNumber)
            return returnItems
        }, [])

        if (collaborators.length === 0) {
            setMessageBar({
                isDisplayed: true,
                message: 'Il faut sélectionner au moins un collaborateur',
                type: MessageBarType.error,
            })
            return
        }
        setModal({
            show: true,
            title: `Exporter ${collaborators.length} collaborateur(s)`,
            subTitle: 'Êtes-vous certain de vouloir exporter le(s) collaborateur(s)? Cette action est définitive.',
            callback: () => this.setState({
                status: Status.PENDING,
            }, async () => {
                try {
                    this.createExportHandler = exportHandler.create({ ...exportDto, collaborators })

                    await this.createExportHandler.fetch()

                    this.selectAllMatricules(initItems, false)
                    this.setState({
                        status: Status.RESOLVED,
                        exportDto: new ExportDto(),
                        errorField: new ErrorExportDto(),
                        searchParams: {
                            company: null,
                            analyticalCenter: null,
                            manager: null,
                            areCollaboratorsLeft: null,
                            onlyDriver: null,
                        },
                    })
                    setMessageBar({
                        isDisplayed: true,
                        message: "L'export est en cours d'exécution.",
                        type: MessageBarType.success,
                    })
                } catch (error) {
                    switch (error?.constructor) {
                        case CancelRequestError:
                        case UnauthorizedError: break
                        case InvalidEntityError:
                            this.setState({
                                errorField: /** @type {InvalidEntityError<ErrorExportDto>} */(error).errorField,
                                status: Status.REJECTED,
                            })
                            if (error.errorField.collaborators)
                                setMessageBar({
                                    isDisplayed: true,
                                    message: error.errorField.collaborators,
                                    type: MessageBarType.error,
                                })
                            break
                        default:
                            this.setState({ status: Status.REJECTED })
                            // eslint-disable-next-line no-console
                            console.error(error)
                            break
                    }
                }
            }),
        })
    }

    /**
     * select or unSelect all collaborators
     * @param {Collaborator[]} collaborators collaborators
     * @param {boolean} isSelected isSelected
     */
    selectAllMatricules(collaborators, isSelected) {
        const newItems = collaborators.map(x => ({ ...x, isSelected }))
        this.setState({
            items: newItems.map(x => this.flattenObj(x)),
            initItems: newItems,
            isCollaboratorsChecked: isSelected,
        })
    }

    /**
     * Render component
     * @returns {JSX.Element} Element
     */
    render() {
        const {
            columns, status, items, initItems, exportDto, searchParams, filterParams, displayCollaborators,
            isCollaboratorsChecked, analyticalCenters, managers, errorField,
        } = this.state

        const { param } = this.props

        return (
            <main className={styles.export}>
                <form
                    className={classNames(
                        styles['export-form'],
                        { [styles['without-collaborators']]: !displayCollaborators },
                    )}
                    onSubmit={ev => {
                        ev.preventDefault()
                        this.export()
                    }}
                >
                    <Card className={styles['form-card']}>
                        <Columns>
                            <Columns.Column size="half">
                                <TextField
                                    label="Date de début d’export"
                                    placeholder="Date de début d’export"
                                    value={exportDto.startDate ? Time(exportDto.startDate).getLocaleDateString() : ''}
                                    readOnly={status === Status.PENDING}
                                    borderless={status === Status.PENDING}
                                    onChange={(ev, newVal) => this.setState({
                                        exportDto: {
                                            ...exportDto, startDate: isValidDate(new Date(newVal)) ? /** @type {any} */(new Date(newVal)) : '',
                                        },
                                    })}
                                    errorMessage={errorField.startDate}
                                    required
                                    type="date"
                                />
                            </Columns.Column>
                            <Columns.Column size="half">
                                <TextField
                                    label="Date de fin d’export"
                                    placeholder="Date de fin d’export"
                                    value={exportDto.endDate ? Time(exportDto.endDate).getLocaleDateString() : ''}
                                    readOnly={status === Status.PENDING}
                                    borderless={status === Status.PENDING}
                                    onChange={(ev, newVal) => this.setState({
                                        exportDto: {
                                            ...exportDto, endDate: isValidDate(new Date(newVal)) ? /** @type {any} */(new Date(newVal)) : '',
                                        },
                                    })}
                                    errorMessage={errorField.endDate}
                                    required
                                    type="date"
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <Checkbox
                                    label="Afficher les collaborateurs sortis"
                                    styles={{ root: { marginTop: 10 }, label: { fontWeight: 'bold' } }}
                                    checked={searchParams.areCollaboratorsLeft}
                                    onChange={(ev, option) => this.setState({
                                        searchParams: {
                                            ...searchParams, areCollaboratorsLeft: option,
                                        },
                                    })}
                                />
                            </Columns.Column>
                            <Columns.Column size="half">
                                <Checkbox
                                    label="Afficher uniquement les conducteurs"
                                    styles={{ root: { marginTop: 10 }, label: { fontWeight: 'bold' } }}
                                    checked={searchParams.onlyDriver}
                                    onChange={(ev, option) => this.setState({
                                        searchParams: {
                                            ...searchParams, onlyDriver: option,
                                        },
                                    })}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label="Société"
                                    placeholder="Société"
                                    options={param.companies}
                                    selectedKey={searchParams.company}
                                    onChange={(ev, option) => this.onFilterChange('company', option)}
                                    disabled={status === Status.PENDING}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label="Centre analytique"
                                    placeholder="Centre analytique"
                                    options={analyticalCenters}
                                    selectedKey={searchParams.analyticalCenter}
                                    onChange={(ev, option) => this.onFilterChange('analyticalCenter', option)}
                                    disabled={status === Status.PENDING}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label="Manager"
                                    placeholder="Manager"
                                    options={managers}
                                    selectedKey={searchParams.manager}
                                    disabled={status === Status.PENDING}
                                    onChange={(ev, option) => this.onFilterChange('manager', option)}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Label>Matricules</Label>
                        </Columns>
                        <Columns>
                            <Columns.Column>
                                <Checkbox
                                    label="Intégralités des matricules"
                                    checked={isCollaboratorsChecked}
                                    onChange={(ev, newVal) => this.selectAllMatricules(initItems, newVal)}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Label required>Flux</Label>
                        </Columns>
                        <Columns>
                            <Columns.Column>
                                <Checkbox
                                    label="Export Edition SOLID Rubriques"
                                    checked={exportDto.isRubricsExport}
                                    onChange={(ev, newVal) => this.setState({ exportDto: { ...exportDto, isRubricsExport: newVal } })}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column>
                                <Checkbox
                                    label="Export Edition SOLID Temps"
                                    checked={exportDto.isTimeExport}
                                    onChange={(ev, newVal) => this.setState({ exportDto: { ...exportDto, isTimeExport: newVal } })}
                                />
                            </Columns.Column>
                        </Columns>
                        <br />
                        <br />
                        <br />
                        <Columns>
                            <Columns.Column size="half" />
                            <Columns.Column size="half">
                                <DefaultButton
                                    text="Exporter"
                                    style={{ width: '100%' }}
                                    onClick={() => this.submitInput.current.click()}
                                    disabled={status === Status.PENDING || (!exportDto.isRubricsExport && !exportDto.isTimeExport)}
                                />
                            </Columns.Column>
                        </Columns>
                    </Card>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <button
                        type="submit"
                        style={{ display: 'none' }}
                        ref={this.submitInput}
                        tabIndex={-1}
                    />
                </form>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Card>
                        <Columns>
                            <Columns.Column size="one-third">
                                <TextField
                                    label="Matricule"
                                    disabled={status === Status.PENDING}
                                    value={filterParams.registrationNumber}
                                    onChange={(ev, newVal) => this.setState(
                                        { filterParams: { ...filterParams, registrationNumber: newVal } }, () => this.setFilterCollaborators(initItems),
                                    )}
                                />
                            </Columns.Column>
                            <Columns.Column size="one-third">
                                <TextField
                                    label="Prénom"
                                    disabled={status === Status.PENDING}
                                    value={filterParams.firstname}
                                    onChange={(ev, newVal) => this.setState(
                                        { filterParams: { ...filterParams, firstname: newVal?.toUpperCase() } }, () => this.setFilterCollaborators(initItems),
                                    )}
                                />
                            </Columns.Column>
                            <Columns.Column size="one-third">
                                <TextField
                                    label="Nom"
                                    disabled={status === Status.PENDING}
                                    value={filterParams.lastname}
                                    onChange={(ev, newVal) => this.setState(
                                        { filterParams: { ...filterParams, lastname: newVal?.toUpperCase() } }, () => this.setFilterCollaborators(initItems),
                                    )}
                                />
                            </Columns.Column>
                        </Columns>

                    </Card>
                    <br />
                    {displayCollaborators && (
                        <Card className={styles.collaborators}>
                            <div className={styles['collaborators-list']}>
                                <ShimmeredDetailsList
                                    items={items}
                                    columns={columns}
                                    selectionMode={SelectionMode.none}
                                    onShouldVirtualize={() => true}
                                    enableShimmer={status === Status.PENDING}
                                    layoutMode={DetailsListLayoutMode.justified}
                                    constrainMode={ConstrainMode.unconstrained}
                                    // onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props })}
                                    onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props, styles: { root: { paddingTop: 0 } } })}
                                    // eslint-disable-next-line react/no-unstable-nested-components
                                    onRenderRow={(props, defaultRender) => (
                                        <button
                                            style={{ all: 'unset' }}
                                            type="button"
                                            onClick={e => {
                                                e.preventDefault()
                                                const newItems = initItems.map(item => {
                                                    if (item.registrationNumber === props.item.registrationNumber)
                                                        return {
                                                            ...item,
                                                            isSelected: !item.isSelected,
                                                        }
                                                    return item
                                                })
                                                this.setFilterCollaborators(newItems)
                                            }}
                                        >
                                            <div>
                                                {defaultRender(props)}
                                            </div>
                                        </button>
                                    )}
                                />
                                {!searchParams.company
                                    && (
                                        <Text styles={{ root: { marginLeft: '1em' } }}>
                                            Séléctionner une société pour récupérer la liste des collaborateurs.
                                        </Text>
                                    )}
                                {!!searchParams.company && !items?.length && [Status.RESOLVED, Status.REJECTED].includes(status)
                                    && <Text styles={{ root: { fontStyle: 'italic', marginLeft: '1em' } }}>Aucun résultat trouvé</Text>}
                            </div>
                        </Card>
                    )}

                </div>
            </main>
        )
    }
}

Export.prototype.flattenObj = flattenObj
Export.prototype.handleRenderColumn = handleRenderColumn
